import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Stack,
  MenuItem,
  Divider,
  Tab,
  Tabs,
  Avatar,
  Paper,
  IconButton,
  Tooltip,
  Badge,
  Fade,
  Zoom,
  Switch,
  FormControlLabel,
  InputAdornment,
  Alert,
  Snackbar,
  Fab,
} from "@mui/material";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import EventSeatIcon from "@mui/icons-material/EventSeat";
import SettingsIcon from "@mui/icons-material/Settings";
import PersonIcon from "@mui/icons-material/Person";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import NotesIcon from "@mui/icons-material/Notes";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useNotify } from "react-admin";
import { useNavigate } from "react-router-dom";

const API = "https://panel.haymestasarim.com/api";

/* ================= Renk Paleti ================= */
const COLORS = {
  primary: "#1976d2",
  secondary: "#f57c00",
  success: "#2e7d32",
  warning: "#ed6c02",
  error: "#d32f2f",
  info: "#0288d1",
  background: {
    light: "#f5f5f5",
    dark: "#333333",
  },
};

/* ================= I18N ================= */
const I18N = {
  tr: {
    title: "Restoran Masa Yönetimi",
    desc: "Masa rezervasyonlarını yönetin ve masa planını düzenleyin",
    selectDate: "Tarih Seç",
    capacity: "Kapasite",
    table: "Masa",
    status: "Durum",
    all: "Tümü",
    available: "Müsait",
    reserved: "Rezerve",
    completed: "Tamamlandı",
    pending: "Onay Bekliyor",
    approved: "Onaylandı",
    rejected: "Reddedildi",
    cancelled: "İptal Edildi",
    close: "Kapat",
    loading: "Yükleniyor...",
    empty: "Kayıt bulunamadı.",
    statsTotal: "Toplam Masa",
    statsAvailable: "Müsait",
    statsReserved: "Rezerve",
    statsCompleted: "Tamamlanan",
    appointmentTime: "Rezervasyon Saati",
    customer: "Müşteri",
    notes: "Notlar",
    approve: "Onayla",
    reject: "Reddet",
    cancel: "İptal Et",
    complete: "Tamamla",
    refresh: "Yenile",
    details: "Detaylar",
    today: "Bugün",
    tomorrow: "Yarın",
    thisWeek: "Bu Hafta",
    filters: "Filtreler",
    clearFilters: "Filtreleri Temizle",
    salon: "Salon",
    allSalons: "Tüm Salonlar",
    save: "Kaydet",
    saving: "Kaydediliyor...",
    saved: "Ayarlar kaydedildi",
    error: "İşlem başarısız",
    addSalon: "Salon Ekle",
    addTable: "Masa Ekle",
    salonName: "Salon Adı",
    tableName: "Masa Adı",
    settings: "Masa Planı Düzenle",
    viewMode: "Rezervasyon Görünümü",
    editMode: "Düzenleme Modu",
    minutes: "dk",
    days: "gün",
    monday: "Pazartesi",
    tuesday: "Salı",
    wednesday: "Çarşamba",
    thursday: "Perşembe",
    friday: "Cuma",
    saturday: "Cumartesi",
    sunday: "Pazar",
    open: "Açık",
    closed: "Kapalı",
    workingHours: "Çalışma Saatleri",
    enableBooking: "Randevu Sistemini Aktif Et",
    slotDuration: "Randevu Süresi",
    gapMinutes: "Randevu Arası",
    yourBooking: "Rezervasyonunuz",
    alreadyHaveAppointment: "Bu saatte zaten rezervasyonunuz var",
  },
  en: {
    title: "Restaurant Table Management",
    desc: "Manage table reservations and table layout",
    selectDate: "Select Date",
    capacity: "Capacity",
    table: "Table",
    status: "Status",
    all: "All",
    available: "Available",
    reserved: "Reserved",
    completed: "Completed",
    pending: "Pending",
    approved: "Approved",
    rejected: "Rejected",
    cancelled: "Cancelled",
    close: "Close",
    loading: "Loading...",
    empty: "No records found.",
    statsTotal: "Total Tables",
    statsAvailable: "Available",
    statsReserved: "Reserved",
    statsCompleted: "Completed",
    appointmentTime: "Reservation Time",
    customer: "Customer",
    notes: "Notes",
    approve: "Approve",
    reject: "Reject",
    cancel: "Cancel",
    complete: "Complete",
    refresh: "Refresh",
    details: "Details",
    today: "Today",
    tomorrow: "Tomorrow",
    thisWeek: "This Week",
    filters: "Filters",
    clearFilters: "Clear Filters",
    salon: "Hall",
    allSalons: "All Halls",
    save: "Save",
    saving: "Saving...",
    saved: "Settings saved",
    error: "Operation failed",
    addSalon: "Add Hall",
    addTable: "Add Table",
    salonName: "Hall Name",
    tableName: "Table Name",
    settings: "Edit Table Layout",
    viewMode: "Reservation View",
    editMode: "Edit Mode",
    minutes: "min",
    days: "days",
    monday: "Monday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
    thursday: "Thursday",
    friday: "Friday",
    saturday: "Saturday",
    sunday: "Sunday",
    open: "Open",
    closed: "Closed",
    workingHours: "Working Hours",
    enableBooking: "Enable Booking System",
    slotDuration: "Slot Duration",
    gapMinutes: "Gap Between",
    yourBooking: "Your Reservation",
    alreadyHaveAppointment: "You already have a reservation at this time",
  },
  de: {
    title: "Restaurant Tischverwaltung",
    desc: "Verwalten Sie Tischreservierungen und Tischanordnung",
    selectDate: "Datum auswählen",
    capacity: "Kapazität",
    table: "Tisch",
    status: "Status",
    all: "Alle",
    available: "Frei",
    reserved: "Reserviert",
    completed: "Abgeschlossen",
    pending: "Ausstehend",
    approved: "Genehmigt",
    rejected: "Abgelehnt",
    cancelled: "Storniert",
    close: "Schließen",
    loading: "Laden...",
    empty: "Keine Einträge gefunden.",
    statsTotal: "Tische Gesamt",
    statsAvailable: "Frei",
    statsReserved: "Reserviert",
    statsCompleted: "Abgeschlossen",
    appointmentTime: "Reservierungszeit",
    customer: "Kunde",
    notes: "Notizen",
    approve: "Genehmigen",
    reject: "Ablehnen",
    cancel: "Stornieren",
    complete: "Abschließen",
    refresh: "Aktualisieren",
    details: "Details",
    today: "Heute",
    tomorrow: "Morgen",
    thisWeek: "Diese Woche",
    filters: "Filter",
    clearFilters: "Filter löschen",
    salon: "Saal",
    allSalons: "Alle Säle",
    save: "Speichern",
    saving: "Speichern...",
    saved: "Einstellungen gespeichert",
    error: "Vorgang fehlgeschlagen",
    addSalon: "Saal hinzufügen",
    addTable: "Tisch hinzufügen",
    salonName: "Saalname",
    tableName: "Tischname",
    settings: "Tischanordnung bearbeiten",
    viewMode: "Reservierungsansicht",
    editMode: "Bearbeitungsmodus",
    minutes: "Min.",
    days: "Tage",
    monday: "Montag",
    tuesday: "Dienstag",
    wednesday: "Mittwoch",
    thursday: "Donnerstag",
    friday: "Freitag",
    saturday: "Samstag",
    sunday: "Sonntag",
    open: "Geöffnet",
    closed: "Geschlossen",
    workingHours: "Arbeitszeiten",
    enableBooking: "Buchungssystem aktivieren",
    slotDuration: "Termindauer",
    gapMinutes: "Pause zwischen",
    yourBooking: "Ihre Reservierung",
    alreadyHaveAppointment: "Sie haben bereits eine Reservierung zu dieser Zeit",
  },
};

function getLang() {
  const l = localStorage.getItem("app_lang") || "tr";
  return ["tr", "en", "de"].includes(l) ? l : "tr";
}

function authHeaders() {
  return {
    Authorization: "Bearer " + localStorage.getItem("token"),
    "Content-Type": "application/json",
  };
}

const STATUS_CONFIG = {
  available: { color: "success", icon: <CheckCircleIcon />, label: "available" },
  reserved: { color: "warning", icon: <PendingIcon />, label: "reserved" },
  pending: { color: "warning", icon: <PendingIcon />, label: "pending" },
  approved: { color: "info", icon: <CheckCircleIcon />, label: "approved" },
  completed: { color: "success", icon: <CheckCircleIcon />, label: "completed" },
  rejected: { color: "error", icon: <CancelIcon />, label: "rejected" },
  cancelled: { color: "error", icon: <CancelIcon />, label: "cancelled" },
};

function StatCard({ label, value, icon, color }) {
  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Avatar sx={{ bgcolor: color, color: "white" }}>{icon}</Avatar>
          <Box>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="h5" fontWeight={900}>{value}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function DraggableTable({ table, salonId, onUpdate, onDelete, isDragging, onDragStart, activeColor }) {
  return (
    <Box
      onMouseDown={(e) => onDragStart(e, salonId, table.id)}
      sx={{
        position: "absolute",
        left: table.x || 50,
        top: table.y || 50,
        width: 70,
        height: 70,
        borderRadius: 2,
        bgcolor: table.active ? activeColor : "#aaa",
        color: "white",
        cursor: isDragging ? "grabbing" : "grab",
        p: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: 3,
        transition: "box-shadow 0.2s",
        "&:hover": { 
          boxShadow: 6,
          "& .delete-button": { display: "block" }
        },
        zIndex: isDragging ? 1000 : 1,
      }}
    >
      <Typography fontWeight={900} fontSize="0.9rem">{table.name}</Typography>
      <Typography variant="caption">{table.capacity} 👤</Typography>
      <IconButton
        className="delete-button"
        size="small"
        sx={{
          position: "absolute",
          top: -8,
          right: -8,
          bgcolor: "error.main",
          color: "white",
          p: 0.5,
          display: "none",
          "&:hover": { bgcolor: "error.dark" }
        }}
        onClick={(e) => {
          e.stopPropagation();
          onDelete(salonId, table.id);
        }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
      <DragIndicatorIcon sx={{ position: "absolute", bottom: 2, right: 2, fontSize: 14, opacity: 0.7 }} />
    </Box>
  );
}

export default function RestaurantTablesPage() {
  const notify = useNotify();
  const navigate = useNavigate();
  const T = I18N[getLang()];

  // Modlar
  const [mode, setMode] = useState("view"); // "view" veya "edit"
  
  // Tarih ve Filtreler
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [salonFilter, setSalonFilter] = useState("all");
  const [selectedTable, setSelectedTable] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [quickDate, setQuickDate] = useState("today");

  // Ayarlar
  const [settings, setSettings] = useState({
    randevu_aktif: false,
    slot_suresi: 30,
    gap_minutes: 0,
  });

  // Çalışma Saatleri
  const [workingHours, setWorkingHours] = useState(() => {
    const hours = {};
    const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
    days.forEach(day => {
      hours[day] = {
        enabled: day !== "sunday",
        start: "09:00",
        end: "18:00",
      };
    });
    return hours;
  });

  // Masa Planı
  const [salons, setSalons] = useState([]);
  const [draggingTable, setDraggingTable] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Veri Yükleme
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Masa durumlarını getir
      const tablesRes = await fetch(`${API}/randevu/restaurant/tables/status?date=${date}`, {
        headers: authHeaders(),
      });
      const tablesJson = await tablesRes.json();
      setTables(tablesJson?.data || []);
      
      // Ayarları getir
      const settingsRes = await fetch(`${API}/randevu/settings`, { headers: authHeaders() });
      const settingsJson = await settingsRes.json();
      const data = settingsJson?.data || {};
      
      setSettings({
        randevu_aktif: data.randevu_aktif || false,
        slot_suresi: data.slot_suresi || 30,
        gap_minutes: data.gap_minutes || 0,
      });
      
      if (data.hours) {
        setWorkingHours(prev => ({ ...prev, ...data.hours }));
      }
      
      // Masa planını getir
      const planRes = await fetch(`${API}/randevu/restaurant/settings`, { headers: authHeaders() });
      const planJson = await planRes.json();
      setSalons(planJson?.data || []);
      
    } catch (error) {
      notify(T.error, { type: "error" });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [date]);

  const handleQuickDate = (type) => {
    const today = new Date();
    if (type === "today") {
      setDate(today.toISOString().slice(0, 10));
    } else if (type === "tomorrow") {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      setDate(tomorrow.toISOString().slice(0, 10));
    }
    setQuickDate(type);
  };

  const clearFilters = () => {
    setStatusFilter("all");
    setSalonFilter("all");
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  // Masa işlemleri
  const addSalon = () => {
    setSalons([
      ...salons,
      {
        id: "temp_" + Date.now(),
        name: `Salon ${salons.length + 1}`,
        tables: [],
      },
    ]);
  };

  const addTable = (salonId) => {
    setSalons(salons.map(salon => {
      if (salon.id === salonId) {
        return {
          ...salon,
          tables: [
            ...salon.tables,
            {
              id: "temp_" + Date.now(),
              name: `M${salon.tables.length + 1}`,
              capacity: 4,
              active: true,
              x: 50 + (salon.tables.length * 80),
              y: 50 + (salon.tables.length * 80),
            },
          ],
        };
      }
      return salon;
    }));
  };

  const updateTable = (salonId, tableId, updates) => {
    setSalons(salons.map(salon =>
      salon.id === salonId
        ? {
            ...salon,
            tables: salon.tables.map(table =>
              table.id === tableId ? { ...table, ...updates } : table
            ),
          }
        : salon
    ));
  };

  const deleteTable = (salonId, tableId) => {
    setSalons(salons.map(salon =>
      salon.id === salonId
        ? { ...salon, tables: salon.tables.filter(t => t.id !== tableId) }
        : salon
    ));
  };

  const deleteSalon = (salonId) => {
    setSalons(salons.filter(s => s.id !== salonId));
  };

  // Sürükleme işlemleri
  const handleDragStart = (e, salonId, tableId) => {
    if (mode !== "edit") return;
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const table = salons.find(s => s.id === salonId)?.tables.find(t => t.id === tableId);
    if (!table) return;

    const startLeft = table.x || 50;
    const startTop = table.y || 50;

    const onMouseMove = (moveEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      updateTable(salonId, tableId, {
        x: Math.max(0, Math.min(800, startLeft + dx)),
        y: Math.max(0, Math.min(800, startTop + dy)),
      });
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // Ayarları kaydet
  const saveSettings = async () => {
    try {
      setSaving(true);
      
      // Randevu ayarlarını kaydet
      await fetch(`${API}/randevu/settings`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({
          ...settings,
          hours: workingHours,
        }),
      });
      
      // Masa planını kaydet
      await fetch(`${API}/randevu/restaurant/settings`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ salons }),
      });
      
      setSnackbar({ open: true, message: T.saved, severity: "success" });
      
    } catch (error) {
      setSnackbar({ open: true, message: T.error, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  // Durum güncelleme
  const updateStatus = async (bookingId, newStatus) => {
    try {
      const res = await fetch(`${API}/randevu/bookings/${bookingId}/${newStatus}`, {
        method: "POST",
        headers: authHeaders(),
      });
      
      if (res.ok) {
        notify(T[newStatus] || newStatus, { type: "success" });
        fetchData();
        setDetailsOpen(false);
      }
    } catch (error) {
      notify(T.error, { type: "error" });
    }
  };

  // İstatistikler
  const stats = useMemo(() => ({
    total: tables.length,
    available: tables.filter(t => t.status === "available").length,
    reserved: tables.filter(t => ["reserved", "approved", "pending"].includes(t.status)).length,
    completed: tables.filter(t => t.status === "completed").length,
  }), [tables]);

  // Filtreleme
  const filteredTables = useMemo(() => {
    let filtered = tables;
    
    if (salonFilter !== "all") {
      filtered = filtered.filter(t => t.salon === salonFilter);
    }
    
    if (statusFilter !== "all") {
      filtered = filtered.filter(t => t.status === statusFilter);
    }
    
    return filtered;
  }, [tables, salonFilter, statusFilter]);

  // Salonlara göre grupla
  const groupedTables = useMemo(() => {
    const groups = {};
    filteredTables.forEach(table => {
      const salon = table.salon || "Diğer";
      if (!groups[salon]) groups[salon] = [];
      groups[salon].push(table);
    });
    return groups;
  }, [filteredTables]);

  // Benzersiz salon listesi
  const salonsList = useMemo(() => {
    const unique = [...new Set(tables.map(t => t.salon))];
    return ["all", ...unique];
  }, [tables]);

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", bgcolor: "#f5f5f5", p: 3 }}>
      
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)` }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems="center" spacing={2}>
          <Box>
            <Typography variant="h4" fontWeight={900} color="white">
              🍽️ {T.title}
            </Typography>
            <Typography variant="body2" color="white" sx={{ opacity: 0.9 }}>
              {T.desc}
            </Typography>
          </Box>
          
          <Stack direction="row" spacing={1}>
            <Tooltip title={T.refresh}>
              <IconButton onClick={handleRefresh} sx={{ color: "white", bgcolor: "rgba(255,255,255,0.1)" }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            
            <Button
              variant="contained"
              onClick={() => setMode(mode === "view" ? "edit" : "view")}
              sx={{
                bgcolor: "white",
                color: mode === "edit" ? COLORS.secondary : COLORS.primary,
                fontWeight: 700,
              }}
            >
              {mode === "view" ? T.settings : T.viewMode}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* İstatistikler */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard label={T.statsTotal} value={stats.total} icon={<TableRestaurantIcon />} color={COLORS.primary} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label={T.statsAvailable} value={stats.available} icon={<CheckCircleIcon />} color={COLORS.success} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label={T.statsReserved} value={stats.reserved} icon={<PendingIcon />} color={COLORS.warning} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label={T.statsCompleted} value={stats.completed} icon={<CheckCircleIcon />} color={COLORS.info} />
        </Grid>
      </Grid>

      {/* Filtreler */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant={quickDate === "today" ? "contained" : "outlined"}
                onClick={() => handleQuickDate("today")}
                sx={{ color: quickDate === "today" ? "white" : COLORS.primary }}
              >
                {T.today}
              </Button>
              <Button
                size="small"
                variant={quickDate === "tomorrow" ? "contained" : "outlined"}
                onClick={() => handleQuickDate("tomorrow")}
                sx={{ color: quickDate === "tomorrow" ? "white" : COLORS.primary }}
              >
                {T.tomorrow}
              </Button>
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                type="date"
                label={T.selectDate}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ minWidth: 200 }}
              />

              <TextField
                select
                label={T.salon}
                value={salonFilter}
                onChange={(e) => setSalonFilter(e.target.value)}
                size="small"
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="all">{T.allSalons}</MenuItem>
                {salonsList.filter(s => s !== "all").map(salon => (
                  <MenuItem key={salon} value={salon}>{salon}</MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label={T.status}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                size="small"
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="all">{T.all}</MenuItem>
                <MenuItem value="available">{T.available}</MenuItem>
                <MenuItem value="reserved">{T.reserved}</MenuItem>
                <MenuItem value="pending">{T.pending}</MenuItem>
                <MenuItem value="approved">{T.approved}</MenuItem>
                <MenuItem value="completed">{T.completed}</MenuItem>
              </TextField>

              {(statusFilter !== "all" || salonFilter !== "all") && (
                <Button variant="text" onClick={clearFilters} sx={{ color: COLORS.secondary }}>
                  {T.clearFilters}
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Ana İçerik */}
      {loading ? (
        <Box sx={{ py: 8, textAlign: "center" }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>{T.loading}</Typography>
        </Box>
      ) : mode === "view" ? (
        // REZERVASYON GÖRÜNÜMÜ
        filteredTables.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary">{T.empty}</Typography>
          </Paper>
        ) : (
          Object.entries(groupedTables).map(([salon, tables]) => (
            <Box key={salon} sx={{ mb: 4 }}>
              <Typography variant="h6" fontWeight={900} sx={{ mb: 2, pl: 1 }}>
                🏠 {salon} ({tables.length})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {tables.map((table) => {
                  const config = STATUS_CONFIG[table.status] || STATUS_CONFIG.available;
                  
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={table.id}>
                      <Card
                        onClick={() => {
                          setSelectedTable(table);
                          setDetailsOpen(true);
                        }}
                        sx={{
                          cursor: "pointer",
                          borderRadius: 2,
                          height: "100%",
                          transition: "all 0.3s",
                          border: `1px solid ${COLORS[config.color]}20`,
                          "&:hover": {
                            boxShadow: `0 8px 16px ${COLORS[config.color]}40`,
                            transform: "translateY(-4px)",
                            border: `1px solid ${COLORS[config.color]}`,
                          },
                        }}
                      >
                        <CardContent>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography fontWeight={900} sx={{ color: COLORS[config.color] }}>
                              {table.name}
                            </Typography>
                            <TableRestaurantIcon sx={{ color: COLORS[config.color] }} />
                          </Stack>

                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                            <EventSeatIcon fontSize="small" sx={{ color: COLORS[config.color] }} />
                            <Typography variant="body2">{T.capacity}: {table.capacity}</Typography>
                          </Stack>

                          {table.baslangic && (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                              <AccessTimeIcon fontSize="small" sx={{ color: COLORS[config.color] }} />
                              <Typography variant="body2">
                                {table.baslangic} - {table.bitis}
                              </Typography>
                            </Stack>
                          )}

                          <Chip
                            size="small"
                            icon={config.icon}
                            label={T[config.label]}
                            color={config.color}
                            sx={{ mt: 2 }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ))
        )
      ) : (
        // DÜZENLEME GÖRÜNÜMÜ (MASA PLANI)
        <Stack spacing={3}>
          {/* Randevu Ayarları */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                ⚙️ {T.settings}
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.randevu_aktif}
                        onChange={(e) => setSettings({ ...settings, randevu_aktif: e.target.checked })}
                      />
                    }
                    label={T.enableBooking}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label={T.slotDuration}
                    value={settings.slot_suresi}
                    onChange={(e) => setSettings({ ...settings, slot_suresi: parseInt(e.target.value) || 30 })}
                    InputProps={{ endAdornment: <InputAdornment position="end">{T.minutes}</InputAdornment> }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label={T.gapMinutes}
                    value={settings.gap_minutes}
                    onChange={(e) => setSettings({ ...settings, gap_minutes: parseInt(e.target.value) || 0 })}
                    InputProps={{ endAdornment: <InputAdornment position="end">{T.minutes}</InputAdornment> }}
                    size="small"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Çalışma Saatleri */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                🕒 {T.workingHours}
              </Typography>
              
              <Grid container spacing={2}>
                {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((day) => {
                  const h = workingHours[day] || { enabled: false, start: "09:00", end: "18:00" };
                  
                  return (
                    <Grid item xs={12} sm={6} md={4} key={day}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack spacing={1}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography fontWeight={600}>{T[day]}</Typography>
                            <Chip
                              size="small"
                              label={h.enabled ? T.open : T.closed}
                              color={h.enabled ? "success" : "default"}
                              onClick={() => setWorkingHours({
                                ...workingHours,
                                [day]: { ...h, enabled: !h.enabled },
                              })}
                            />
                          </Stack>
                          
                          {h.enabled && (
                            <Stack direction="row" spacing={1}>
                              <TextField
                                type="time"
                                size="small"
                                value={h.start}
                                onChange={(e) => setWorkingHours({
                                  ...workingHours,
                                  [day]: { ...h, start: e.target.value },
                                })}
                                InputLabelProps={{ shrink: true }}
                              />
                              <TextField
                                type="time"
                                size="small"
                                value={h.end}
                                onChange={(e) => setWorkingHours({
                                  ...workingHours,
                                  [day]: { ...h, end: e.target.value },
                                })}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Stack>
                          )}
                        </Stack>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>

          {/* Masa Planı */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>
                  🪑 {T.tables}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={addSalon}
                  sx={{ bgcolor: COLORS.primary }}
                >
                  {T.addSalon}
                </Button>
              </Stack>

              {salons.length === 0 ? (
                <Alert severity="info">Henüz salon eklenmemiş.</Alert>
              ) : (
                salons.map((salon) => (
                  <Card key={salon.id} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <TextField
                          label={T.salonName}
                          value={salon.name}
                          onChange={(e) => {
                            setSalons(salons.map(s =>
                              s.id === salon.id ? { ...s, name: e.target.value } : s
                            ));
                          }}
                          size="small"
                          sx={{ minWidth: 200 }}
                        />
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => addTable(salon.id)}
                            sx={{ color: COLORS.primary }}
                          >
                            {T.addTable}
                          </Button>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => deleteSalon(salon.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </Stack>

                      {/* Masa Grid */}
                      <Paper
                        variant="outlined"
                        sx={{
                          position: "relative",
                          height: 400,
                          bgcolor: "#f9f9f9",
                          overflow: "auto",
                          borderRadius: 2,
                          border: `2px dashed ${COLORS.primary}40`,
                        }}
                      >
                        {salon.tables.map((table) => (
                          <DraggableTable
                            key={table.id}
                            table={table}
                            salonId={salon.id}
                            onUpdate={updateTable}
                            onDelete={deleteTable}
                            onDragStart={handleDragStart}
                            activeColor={COLORS.primary}
                          />
                        ))}
                      </Paper>
                    </CardContent>
                  </Card>
                ))
              )}
            </CardContent>
          </Card>

          {/* Kaydet Butonu */}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              size="large"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={saveSettings}
              disabled={saving}
              sx={{ bgcolor: COLORS.primary, px: 4, py: 1.5 }}
            >
              {saving ? T.saving : T.save}
            </Button>
          </Box>
        </Stack>
      )}

      {/* Detay Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: COLORS.primary, color: "white" }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <TableRestaurantIcon />
            <Typography variant="h6">{selectedTable?.name}</Typography>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          {selectedTable && (
            <Stack spacing={3}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: COLORS.background.light }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: COLORS.primary, color: "white" }}>
                    <EventSeatIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {T.capacity}
                    </Typography>
                    <Typography variant="h6">{selectedTable.capacity} Kişi</Typography>
                  </Box>
                </Stack>
              </Paper>

              {selectedTable.baslangic && (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: COLORS.background.light }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: COLORS.primary, color: "white" }}>
                      <AccessTimeIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {T.appointmentTime}
                      </Typography>
                      <Typography variant="h6">
                        {selectedTable.baslangic} - {selectedTable.bitis}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              )}

              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {T.status}
                </Typography>
                <Chip
                  icon={STATUS_CONFIG[selectedTable.status]?.icon}
                  label={T[STATUS_CONFIG[selectedTable.status]?.label]}
                  color={STATUS_CONFIG[selectedTable.status]?.color}
                  size="medium"
                />
              </Box>

              {selectedTable.booking_id && (
                <>
                  <Divider />
                  <Typography variant="h6">{T.customer}</Typography>
                  
                  <TextField
                    fullWidth
                    label="Müşteri"
                    value={selectedTable.musteri_ad || "Belirtilmemiş"}
                    InputProps={{ readOnly: true }}
                    size="small"
                  />
                  
                  <TextField
                    fullWidth
                    label="Telefon"
                    value={selectedTable.musteri_telefon || "Belirtilmemiş"}
                    InputProps={{ readOnly: true }}
                    size="small"
                  />
                  
                  {selectedTable.kullanici_notu && (
                    <TextField
                      fullWidth
                      label={T.notes}
                      value={selectedTable.kullanici_notu}
                      multiline
                      rows={2}
                      InputProps={{ readOnly: true }}
                    />
                  )}
                </>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          {selectedTable?.booking_id && (
            <>
              {selectedTable.status === "pending" && (
                <>
                  <Button
                    variant="contained"
                    sx={{ bgcolor: COLORS.success }}
                    onClick={() => updateStatus(selectedTable.booking_id, "approve")}
                  >
                    {T.approve}
                  </Button>
                  <Button
                    variant="contained"
                    sx={{ bgcolor: COLORS.error }}
                    onClick={() => updateStatus(selectedTable.booking_id, "reject")}
                  >
                    {T.reject}
                  </Button>
                </>
              )}
              {selectedTable.status === "approved" && (
                <>
                  <Button
                    variant="contained"
                    sx={{ bgcolor: COLORS.success }}
                    onClick={() => updateStatus(selectedTable.booking_id, "complete")}
                  >
                    {T.complete}
                  </Button>
                  <Button
                    variant="outlined"
                    sx={{ color: COLORS.error, borderColor: COLORS.error }}
                    onClick={() => updateStatus(selectedTable.booking_id, "cancel")}
                  >
                    {T.cancel}
                  </Button>
                </>
              )}
            </>
          )}
          <Button onClick={() => setDetailsOpen(null)} variant="outlined">
            {T.close}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}